import React, { Component } from "react";
import axios from "axios";
import { connect } from "react-redux";
import { Form, Formik } from "formik";
import { Redirect } from "react-router-dom";
import * as Sentry from "@sentry/browser";
import * as Yup from "yup";

import Checkbox from "../../components/Checkbox";
import createApiService from "../../network";
import i18n from "../../constants/i18n";
import modalTypes from "../../constants/modalTypes";
import routeNames from "../../constants/routeNames";
import { extractApiErrorMessage } from "../../helpers";
import ValidationError from "../../helpers/ValidationError";
import { fetchUser } from "../../actions/user";
import { showModal } from "../../actions/modals";
import { showToast } from "../../actions/toasts";
import styles from "./IlomeConsent.module.scss";

class IlomeConsentRoute extends Component {
    api = createApiService(axios);

    validationSchema = Yup.object().shape({
        ilomeConsent: Yup.boolean().oneOf([true], i18n.validation.required),
    });

    onSubmit = async (values, formikBag) => {
        try {
            await this.api.updateIlomeConsent(values);
            await this.props.dispatch(fetchUser());

            this.props.dispatch(
                showToast({
                    body: i18n.ilomeConsentPage.successMessage,
                    title: i18n.generic.success,
                    themeClass: "is-success",
                })
            );

            this.props.history.push(routeNames.app);
        } catch (e) {
            if (e instanceof ValidationError) {
                formikBag.setErrors(e.errors);
            } else {
                Sentry.captureException(e);
                console.error(e);
                this.props.dispatch(
                    showToast({
                        body: extractApiErrorMessage(e),
                        title: "Error",
                        themeClass: "is-danger",
                    })
                );
            }
        } finally {
            formikBag.setSubmitting(false);
        }
    };

    onIlomeInfoClick = () => {
        this.props.dispatch(
            showModal({
                type: modalTypes.ILOME_PDF,
                props: {}
            })
        );
    };

    render() {
        if (
            !this.props.user.requiresIlomeConsent ||
            this.props.user.ilomeConsent
        ) {
            return <Redirect to={routeNames.app} />;
        }

        const ilomeConsentLocation = this.props.user.ilomeConsentLocation;
        const locationExplanation = ilomeConsentLocation
            ? i18n.ilomeConsentPage.locationExplanation
                  .split("{crematoriumName}")
                  .join(ilomeConsentLocation.name || "")
                  .replace(
                      "{crematoriumPhoneNumber}",
                      ilomeConsentLocation.phoneNumber || ""
                  )
            : null;

        return (
            <div className="container">
                <div className="columns is-centered">
                    <div className="column is-8">
                        <h1
                            className={`title has-text-centered ${styles.title}`}
                        >
                            {i18n.ilomeConsentPage.title}
                        </h1>
                        <div className={`has-text-centered ${styles.body}`}>
                            <p className={styles.bodyLine}>
                                {i18n.ilomeConsentPage.bodyFirst}
                            </p>
                            <p>{i18n.ilomeConsentPage.bodySecond}</p>
                        </div>
                        <Formik
                            initialValues={{ ilomeConsent: 0 }}
                            validationSchema={this.validationSchema}
                            onSubmit={this.onSubmit}
                            render={({
                                errors,
                                touched,
                                isSubmitting,
                                setFieldValue,
                                handleBlur,
                                values,
                            }) => (
                                <Form>
                                    <div
                                        className={`columns is-centered ${styles.checkboxRow}`}
                                    >
                                        <div className="column is-narrow">
                                            <Checkbox
                                                errors={errors}
                                                touched={touched}
                                                name="ilomeConsent"
                                                value={values.ilomeConsent}
                                                setFieldValue={setFieldValue}
                                                handleBlur={handleBlur}
                                                label={
                                                    i18n.resetPasswordForm
                                                        .ilomeConsentAgreement
                                                }
                                                reversed
                                            />
                                        </div>
                                    </div>

                                    <div className="columns is-centered">
                                        <div className="column is-narrow">
                                            <button
                                                type="submit"
                                                className={`button is-primary is-rounded ${
                                                    isSubmitting
                                                        ? "is-loading"
                                                        : ""
                                                }`}
                                                disabled={
                                                    isSubmitting ||
                                                    !values.ilomeConsent
                                                }
                                            >
                                                {
                                                    i18n.ilomeConsentPage
                                                        .submitButtonLabel
                                                }
                                            </button>
                                        </div>
                                    </div>
                                    {locationExplanation && (
                                        <p
                                            className={`has-text-centered ${styles.locationExplanation}`}
                                        >
                                            {locationExplanation}
                                        </p>
                                    )}
                                    <div
                                        className={`has-text-centered ${styles.extraInfo}`}
                                    >
                                        <p className={styles.bodyLine}>
                                            {i18n.ilomeConsentPage.extraInfo}
                                        </p>
                                        <p>
                                            <button
                                                type="button"
                                                onClick={this.onIlomeInfoClick}
                                                className={styles.linkButton}
                                            >
                                                {
                                                    i18n.ilomeConsentPage
                                                        .extraInfoLinkPrefix
                                                }
                                            </button>
                                            {
                                                i18n.ilomeConsentPage
                                                    .extraInfoLinkMiddle
                                            }
                                            {/* Keep referrer visible for ilome.nl analytics. */}
                                            {/* eslint-disable-next-line react/jsx-no-target-blank */}
                                            <a
                                                href="https://www.ilome.nl"
                                                target="_blank"
                                                rel="noopener"
                                            >
                                                {
                                                    i18n.ilomeConsentPage
                                                        .extraInfoWebsiteLabel
                                                }
                                            </a>
                                            .
                                        </p>
                                    </div>
                                </Form>
                            )}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.user.profile,
});

export default connect(mapStateToProps)(IlomeConsentRoute);
